import React from 'react';
import { Link } from 'react-router-dom';

const navigation = {
  main: [
    { name: 'Home', href: '/' },
    { name: 'Features', href: '/#features' },
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Report', href: '/report' },
    { name: 'Report Gallery', href: '/reports' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ],
  features: [
    { name: 'Clinical Microbiology', href: '/clinical-microbiology' },
    { name: 'Antimicrobial Resistance', href: '/antimicrobial-resistance' },
    { name: 'Treatment Recommendations', href: '/treatment-recommendations' },
    { name: 'Complexity Assessment', href: '/complexity-assessment' },
    { name: 'Monitoring Plans', href: '/monitoring-plans' },
    { name: 'Interactive Reporting', href: '/interactive-reporting' },
  ],
  vendor: [
    { name: 'Vendor Portal', href: 'https://app.reasonshield.com/vendor-portal' },
    { name: 'Documentation', href: '/vendor-documentation' },
    { name: 'Integration Guide', href: '/vendor-integration' },
    { name: 'Support', href: '/vendor-support' },
  ],
  social: [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/showcase/reasonshield',
      icon: (props: React.SVGProps<SVGSVGElement>) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms & Conditions', href: '/terms-and-conditions' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Sitemap', href: '/sitemap.xml' },
  ],
};

const Footer: React.FC = () => {
  return (
    <footer className="bg-section-dark">
      <div className="container-custom py-12 md:py-16 overflow-hidden">
        <div className="xl:grid xl:grid-cols-4 xl:gap-8">
          <div className="space-y-8 xl:col-span-1">
            <div className="flex items-center">
              <Link to="/">
                <img
                  className="h-10"
                  src="/FullLogo_Transparent_NoBuffer.png"
                  alt="ReasonShield"
                />
              </Link>
            </div>
            <p className="text-text text-base">
              Empowering organizations with advanced laboratory data processing and clinical insights through AI-driven technology.
            </p>
            <div className="flex space-x-6">
              {navigation.social.map((item) => (
                <a key={item.name} href={item.href} className="text-secondary hover:text-primary">
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-8 xl:col-span-3 xl:mt-0">
            <div>
              <h3 className="text-sm font-semibold text-secondary tracking-wider uppercase">Navigation</h3>
              <ul  className="mt-4 space-y-4">
                {navigation.main.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-base text-text hover:text-primary">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-secondary tracking-wider uppercase">Features</h3>
              <ul className="mt-4 space-y-4">
                {navigation.features.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-base text-text hover:text-primary">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-secondary tracking-wider uppercase">Vendor Resources</h3>
              <ul className="mt-4 space-y-4">
                {navigation.vendor.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-base text-text hover:text-primary">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-12 xl:mt-16">
          <h3 className="text-sm font-semibold text-secondary tracking-wider uppercase">Contact Us</h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <svg className="flex-shrink-0 w-5 h-5 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:support@reasonshield.com" className="ml-3 text-base text-text hover:text-primary">
                support@reasonshield.com
              </a>
            </div>
            <div className="flex items-center">
              <svg className="flex-shrink-0 w-5 h-5 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <a href="tel:+1-708-540-3586" className="ml-3 text-base text-text hover:text-primary">
                +1 (708) 540-3586
              </a>
            </div>
            <div className="flex items-start">
              <svg className="flex-shrink-0 w-5 h-5 mt-0.5 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="ml-3 text-base text-text">
                1449 S Michigan Ave STE 13207<br />
                Chicago, IL 60605
              </span>
            </div>
          </div>
        </div>
        
        <div className="mt-12 border-t border-border pt-8">
          <div className="flex flex-col md:flex-row md:justify-between items-center">
            <p className="text-base text-secondary">
              &copy; {new Date().getFullYear()} ReasonShield, Inc. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0">
              <ul className="flex flex-wrap gap-4 md:space-x-6">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-base text-secondary hover:text-primary">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 